import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, FileText, Mail, Phone, MessageCircle, ChevronRight, ArrowLeft, HelpCircle, Scale, Lock, Copyright } from 'lucide-react';
import './Legal.css';

const Legal = () => {
  const { page } = useParams();
  const [activeSection, setActiveSection] = useState(page || 'privacy');

  useEffect(() => {
    // Set noindex meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';

    // Update page title
    const titles = {
      privacy: 'Gizlilik Politikası',
      terms: 'Kullanım Koşulları',
      support: 'Destek',
      contact: 'İletişim',
      copyright: 'Telif Hakkı'
    };
    document.title = `${titles[activeSection] || 'Yasal'} | Akik Kuyumculuk`;

    return () => {
      // Cleanup: remove noindex when leaving
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, [activeSection]);

  useEffect(() => {
    if (page) {
      setActiveSection(page);
    }
  }, [page]);

  const sections = [
    { id: 'privacy', name: 'Gizlilik Politikası', icon: <Lock size={20} /> },
    { id: 'terms', name: 'Kullanım Koşulları', icon: <Scale size={20} /> },
    { id: 'support', name: 'Destek', icon: <HelpCircle size={20} /> },
    { id: 'contact', name: 'İletişim', icon: <Mail size={20} /> },
    { id: 'copyright', name: 'Telif Hakkı', icon: <Copyright size={20} /> },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'privacy':
        return <PrivacyPolicy />;
      case 'terms':
        return <TermsOfService />;
      case 'support':
        return <Support />;
      case 'contact':
        return <Contact />;
      case 'copyright':
        return <CopyrightInfo />;
      default:
        return <PrivacyPolicy />;
    }
  };

  return (
    <div className="legal-container">
      <header className="legal-header">
        <div className="header-content">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Ana Sayfa</span>
          </Link>
          <div className="logo">
            <Shield size={32} />
            <span>Akik Kuyumculuk</span>
          </div>
        </div>
      </header>

      <div className="legal-content">
        <nav className="legal-sidebar">
          <h3>Yasal Bilgiler</h3>
          <ul>
            {sections.map(section => (
              <li key={section.id}>
                <button
                  className={activeSection === section.id ? 'active' : ''}
                  onClick={() => setActiveSection(section.id)}
                >
                  {section.icon}
                  <span>{section.name}</span>
                  <ChevronRight size={16} />
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="legal-main">
          {renderContent()}
        </main>
      </div>

      <footer className="legal-footer">
        <p>&copy; {new Date().getFullYear()} Akik Kuyumculuk. Tüm hakları saklıdır.</p>
        <p>Son güncelleme: {new Date().toLocaleDateString('tr-TR')}</p>
      </footer>
    </div>
  );
};

// Privacy Policy Component
const PrivacyPolicy = () => (
  <div className="legal-section">
    <h1><Lock size={28} /> Gizlilik Politikası</h1>
    <p className="last-updated">Son güncelleme: Ocak 2026</p>

    <div className="policy-content">
      <section>
        <h2>1. Giriş</h2>
        <p>
          Akik Kuyumculuk olarak, kullanıcılarımızın gizliliğine büyük önem veriyoruz. Bu Gizlilik Politikası,
          uygulamamızı ve hizmetlerimizi kullanırken kişisel verilerinizin nasıl toplandığını, kullanıldığını,
          saklandığını ve korunduğunu açıklamaktadır.
        </p>
      </section>

      <section>
        <h2>2. Toplanan Veriler</h2>
        <p>Hizmetlerimizi kullanırken aşağıdaki verileri toplayabiliriz:</p>
        <ul>
          <li><strong>Kimlik Bilgileri:</strong> Ad, soyad, e-posta adresi, telefon numarası</li>
          <li><strong>İşlem Bilgileri:</strong> Satın alma geçmişi, ödeme bilgileri, fatura adresi</li>
          <li><strong>Teknik Veriler:</strong> IP adresi, cihaz bilgisi, tarayıcı türü, kullanım istatistikleri</li>
          <li><strong>İletişim Verileri:</strong> Destek talepleri, geri bildirimler</li>
        </ul>
      </section>

      <section>
        <h2>3. Verilerin Kullanım Amaçları</h2>
        <p>Topladığımız verileri aşağıdaki amaçlarla kullanıyoruz:</p>
        <ul>
          <li>Hizmetlerimizi sunmak ve geliştirmek</li>
          <li>Siparişlerinizi işlemek ve teslim etmek</li>
          <li>Müşteri desteği sağlamak</li>
          <li>Güvenlik ve dolandırıcılık önleme</li>
          <li>Yasal yükümlülüklerimizi yerine getirmek</li>
          <li>Size özelleştirilmiş deneyim sunmak</li>
        </ul>
      </section>

      <section>
        <h2>4. Veri Güvenliği</h2>
        <p>
          Kişisel verilerinizin güvenliğini sağlamak için endüstri standardı güvenlik önlemleri uyguluyoruz.
          Bu önlemler arasında şifreleme, güvenli sunucular, erişim kontrolleri ve düzenli güvenlik
          denetimleri yer almaktadır.
        </p>
      </section>

      <section>
        <h2>5. Veri Paylaşımı</h2>
        <p>Kişisel verilerinizi aşağıdaki durumlar dışında üçüncü taraflarla paylaşmıyoruz:</p>
        <ul>
          <li>Yasal zorunluluklar</li>
          <li>Hizmet sağlayıcılarımız (ödeme işlemcileri, kargo şirketleri)</li>
          <li>Açık onayınız olduğunda</li>
        </ul>
      </section>

      <section>
        <h2>6. KVKK Kapsamındaki Haklarınız</h2>
        <p>6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında aşağıdaki haklara sahipsiniz:</p>
        <ul>
          <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
          <li>İşlenmişse buna ilişkin bilgi talep etme</li>
          <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme</li>
          <li>Yurt içinde/yurt dışında aktarıldığı üçüncü kişileri bilme</li>
          <li>Eksik/yanlış işlenmişse düzeltilmesini isteme</li>
          <li>Silinmesini veya yok edilmesini isteme</li>
          <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme</li>
        </ul>
      </section>

      <section>
        <h2>7. Çerezler (Cookies)</h2>
        <p>
          Web sitemiz ve uygulamamız, kullanıcı deneyimini geliştirmek için çerezler kullanmaktadır.
          Çerezler, tarayıcınız aracılığıyla cihazınıza yerleştirilen küçük metin dosyalarıdır.
          Tarayıcı ayarlarınızdan çerezleri yönetebilir veya devre dışı bırakabilirsiniz.
        </p>
      </section>

      <section>
        <h2>8. İletişim</h2>
        <p>Gizlilik politikamızla ilgili sorularınız için bizimle iletişime geçebilirsiniz:</p>
        <div className="contact-info">
          <p><strong>E-posta:</strong> privacy@akikkuyumculuk.com</p>
          <p><strong>Telefon:</strong> +90 (XXX) XXX XX XX</p>
          <p><strong>Adres:</strong> [Şirket Adresi]</p>
        </div>
      </section>

      <section>
        <h2>9. Değişiklikler</h2>
        <p>
          Bu Gizlilik Politikası'nı zaman zaman güncelleyebiliriz. Önemli değişiklikler olduğunda
          sizi bilgilendireceğiz. Güncellemeler yayınlandığında yürürlüğe girer.
        </p>
      </section>
    </div>
  </div>
);

// Terms of Service Component
const TermsOfService = () => (
  <div className="legal-section">
    <h1><Scale size={28} /> Kullanım Koşulları</h1>
    <p className="last-updated">Son güncelleme: Ocak 2026</p>

    <div className="policy-content">
      <section>
        <h2>1. Kabul</h2>
        <p>
          Bu uygulamayı kullanarak, aşağıdaki kullanım koşullarını kabul etmiş sayılırsınız.
          Bu koşulları kabul etmiyorsanız, lütfen uygulamamızı kullanmayınız.
        </p>
      </section>

      <section>
        <h2>2. Hizmet Tanımı</h2>
        <p>
          Akik Kuyumculuk uygulaması, kuyumculuk işletmeleri için stok yönetimi, satış takibi,
          müşteri yönetimi ve raporlama hizmetleri sunan bir yazılım platformudur.
        </p>
      </section>

      <section>
        <h2>3. Kullanıcı Hesapları</h2>
        <ul>
          <li>Hesap oluşturmak için doğru ve güncel bilgiler sağlamalısınız</li>
          <li>Hesap güvenliğinden siz sorumlusunuz</li>
          <li>Şifrenizi başkalarıyla paylaşmamalısınız</li>
          <li>Hesabınızda gerçekleşen tüm aktivitelerden sorumlusunuz</li>
        </ul>
      </section>

      <section>
        <h2>4. Kabul Edilebilir Kullanım</h2>
        <p>Uygulamamızı kullanırken aşağıdaki kurallara uymalısınız:</p>
        <ul>
          <li>Yasalara ve düzenlemelere uygun hareket etmek</li>
          <li>Başkalarının haklarına saygı göstermek</li>
          <li>Sistemi kötüye kullanmamak veya manipüle etmemek</li>
          <li>Zararlı yazılım veya içerik yaymamak</li>
          <li>İzinsiz veri toplamamak veya paylaşmamak</li>
        </ul>
      </section>

      <section>
        <h2>5. Fikri Mülkiyet</h2>
        <p>
          Uygulama ve içeriği, telif hakkı ve diğer fikri mülkiyet yasaları ile korunmaktadır.
          Yazılı izin olmadan kopyalama, dağıtma veya değiştirme yasaktır.
        </p>
      </section>

      <section>
        <h2>6. Sorumluluk Sınırlaması</h2>
        <p>
          Uygulamamız "olduğu gibi" sunulmaktadır. Kullanımdan doğabilecek doğrudan veya dolaylı
          zararlardan sorumlu değiliz. Veri kaybı, iş kaybı veya kar kaybı dahil olmak üzere
          herhangi bir zarardan sorumluluk kabul etmiyoruz.
        </p>
      </section>

      <section>
        <h2>7. Hizmet Değişiklikleri</h2>
        <p>
          Hizmetlerimizi önceden bildirimde bulunmaksızın değiştirme, askıya alma veya sonlandırma
          hakkını saklı tutarız.
        </p>
      </section>

      <section>
        <h2>8. Fesih</h2>
        <p>
          Bu koşulları ihlal etmeniz durumunda hesabınızı askıya alabilir veya sonlandırabiliriz.
          Fesih durumunda verilerinize erişiminiz sona erebilir.
        </p>
      </section>

      <section>
        <h2>9. Uygulanacak Hukuk</h2>
        <p>
          Bu koşullar Türkiye Cumhuriyeti yasalarına tabidir. Uyuşmazlıklar Türkiye mahkemelerinde
          çözümlenecektir.
        </p>
      </section>

      <section>
        <h2>10. İletişim</h2>
        <p>Kullanım koşullarıyla ilgili sorularınız için:</p>
        <div className="contact-info">
          <p><strong>E-posta:</strong> legal@akikkuyumculuk.com</p>
        </div>
      </section>
    </div>
  </div>
);

// Support Component
const Support = () => (
  <div className="legal-section">
    <h1><HelpCircle size={28} /> Destek Merkezi</h1>
    <p className="intro-text">
      Size yardımcı olmak için buradayız. Aşağıdaki yöntemlerle bize ulaşabilirsiniz.
    </p>

    <div className="support-grid">
      <div className="support-card">
        <div className="card-icon">
          <Mail size={32} />
        </div>
        <h3>E-posta Desteği</h3>
        <p>Sorularınızı e-posta ile gönderin, en kısa sürede yanıtlayalım.</p>
        <a href="mailto:destek@akikkuyumculuk.com" className="support-link">
          destek@akikkuyumculuk.com
        </a>
        <span className="response-time">Yanıt süresi: 24 saat içinde</span>
      </div>

      <div className="support-card">
        <div className="card-icon">
          <Phone size={32} />
        </div>
        <h3>Telefon Desteği</h3>
        <p>Acil durumlar için telefon desteğimizi kullanabilirsiniz.</p>
        <a href="tel:+905551234567" className="support-link">
          +90 (555) 123 45 67
        </a>
        <span className="response-time">Pazartesi - Cuma: 09:00 - 18:00</span>
      </div>

      <div className="support-card">
        <div className="card-icon">
          <MessageCircle size={32} />
        </div>
        <h3>Canlı Destek</h3>
        <p>Uygulama içinden canlı destek ile anında yardım alın.</p>
        <span className="support-link">Uygulama içinden erişilebilir</span>
        <span className="response-time">Mesai saatlerinde aktif</span>
      </div>
    </div>

    <div className="faq-section">
      <h2>Sık Sorulan Sorular</h2>

      <div className="faq-item">
        <h4>Şifremi nasıl sıfırlarım?</h4>
        <p>Giriş ekranındaki "Şifremi Unuttum" bağlantısına tıklayarak e-posta adresinize sıfırlama bağlantısı gönderebilirsiniz.</p>
      </div>

      <div className="faq-item">
        <h4>Ürün nasıl eklerim?</h4>
        <p>Ürünler menüsünden "Yeni Ürün Ekle" butonuna tıklayarak ürün bilgilerini girebilirsiniz. Barkod okuyucu ile de hızlıca ürün ekleyebilirsiniz.</p>
      </div>

      <div className="faq-item">
        <h4>Rapor nasıl alırım?</h4>
        <p>Raporlar bölümünden tarih aralığı seçerek satış, stok ve müşteri raporlarınızı görüntüleyebilir ve indirebilirsiniz.</p>
      </div>

      <div className="faq-item">
        <h4>Verilerim güvende mi?</h4>
        <p>Evet, tüm verileriniz şifrelenmiş sunucularda saklanmaktadır. Düzenli yedekleme yapılmaktadır.</p>
      </div>

      <div className="faq-item">
        <h4>Birden fazla mağazayı yönetebilir miyim?</h4>
        <p>Evet, Admin hesabıyla birden fazla mağazayı tek bir panel üzerinden yönetebilirsiniz.</p>
      </div>
    </div>

    <div className="feedback-section">
      <h2>Geri Bildirim</h2>
      <p>
        Uygulamamızı geliştirmemize yardımcı olun. Önerilerinizi ve geri bildirimlerinizi
        <a href="mailto:geribildirim@akikkuyumculuk.com"> geribildirim@akikkuyumculuk.com</a> adresine gönderebilirsiniz.
      </p>
    </div>
  </div>
);

// Contact Component
const Contact = () => (
  <div className="legal-section">
    <h1><Mail size={28} /> İletişim</h1>
    <p className="intro-text">
      Bizimle iletişime geçmek için aşağıdaki bilgileri kullanabilirsiniz.
    </p>

    <div className="contact-grid">
      <div className="contact-card">
        <h3>Genel İletişim</h3>
        <div className="contact-item">
          <Mail size={20} />
          <div>
            <strong>E-posta</strong>
            <a href="mailto:info@akikkuyumculuk.com">info@akikkuyumculuk.com</a>
          </div>
        </div>
        <div className="contact-item">
          <Phone size={20} />
          <div>
            <strong>Telefon</strong>
            <a href="tel:+905551234567">+90 (555) 123 45 67</a>
          </div>
        </div>
      </div>

      <div className="contact-card">
        <h3>Teknik Destek</h3>
        <div className="contact-item">
          <Mail size={20} />
          <div>
            <strong>E-posta</strong>
            <a href="mailto:destek@akikkuyumculuk.com">destek@akikkuyumculuk.com</a>
          </div>
        </div>
        <div className="contact-item">
          <Phone size={20} />
          <div>
            <strong>Destek Hattı</strong>
            <a href="tel:+905557654321">+90 (555) 765 43 21</a>
          </div>
        </div>
      </div>

      <div className="contact-card">
        <h3>Şirket Bilgileri</h3>
        <div className="company-info">
          <p><strong>Akik Kuyumculuk Yazılım A.Ş.</strong></p>
          <p>Vergi No: [VKN]</p>
          <p>Mersis No: [Mersis No]</p>
        </div>
      </div>

      <div className="contact-card">
        <h3>Adres</h3>
        <div className="address-info">
          <p>[Şirket Adresi]</p>
          <p>[İlçe], [İl]</p>
          <p>Türkiye</p>
        </div>
      </div>
    </div>

    <div className="business-hours">
      <h2>Çalışma Saatleri</h2>
      <table>
        <tbody>
          <tr>
            <td>Pazartesi - Cuma</td>
            <td>09:00 - 18:00</td>
          </tr>
          <tr>
            <td>Cumartesi</td>
            <td>10:00 - 14:00</td>
          </tr>
          <tr>
            <td>Pazar</td>
            <td>Kapalı</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

// Copyright Component
const CopyrightInfo = () => (
  <div className="legal-section">
    <h1><Copyright size={28} /> Telif Hakkı Bildirimi</h1>
    <p className="last-updated">Son güncelleme: Ocak 2026</p>

    <div className="policy-content">
      <section>
        <h2>Telif Hakkı</h2>
        <p>
          © {new Date().getFullYear()} Akik Kuyumculuk Yazılım A.Ş. Tüm hakları saklıdır.
        </p>
        <p>
          Bu uygulama ve içeriği, Türkiye Cumhuriyeti ve uluslararası telif hakkı yasaları
          kapsamında korunmaktadır.
        </p>
      </section>

      <section>
        <h2>Koruma Kapsamı</h2>
        <p>Aşağıdakiler dahil ancak bunlarla sınırlı olmamak üzere tüm içerik korunmaktadır:</p>
        <ul>
          <li>Yazılım kodu ve algoritmaları</li>
          <li>Kullanıcı arayüzü tasarımları</li>
          <li>Grafikler, ikonlar ve görseller</li>
          <li>Metinler ve belgeler</li>
          <li>Veritabanı yapıları</li>
          <li>Ticari markalar ve logolar</li>
        </ul>
      </section>

      <section>
        <h2>İzin Verilen Kullanım</h2>
        <p>
          Bu uygulamayı yalnızca satın aldığınız veya lisans aldığınız amaçlar doğrultusunda
          kullanabilirsiniz. İzin verilen kullanım şunları içerir:
        </p>
        <ul>
          <li>İşletmenizin günlük operasyonları için kullanım</li>
          <li>Yasal raporlama ve belgeleme</li>
          <li>Yedekleme amacıyla kopya alma</li>
        </ul>
      </section>

      <section>
        <h2>Yasaklanan Kullanım</h2>
        <p>Aşağıdaki eylemler kesinlikle yasaktır:</p>
        <ul>
          <li>Yazılımın kopyalanması, dağıtılması veya satılması</li>
          <li>Tersine mühendislik veya kaynak kodun çözülmesi</li>
          <li>Yazılımın değiştirilmesi veya türev çalışmalar oluşturulması</li>
          <li>Alt lisans verme veya kiralama</li>
          <li>Ticari markaların izinsiz kullanımı</li>
        </ul>
      </section>

      <section>
        <h2>Üçüncü Taraf Bileşenler</h2>
        <p>
          Bu uygulama, kendi lisanslarına tabi üçüncü taraf açık kaynak bileşenler içerebilir.
          Bu bileşenlerin listesi ve lisansları talep üzerine sağlanabilir.
        </p>
      </section>

      <section>
        <h2>İhlal Bildirimi</h2>
        <p>
          Telif hakkı ihlali tespit ederseniz veya şikayetiniz varsa, lütfen aşağıdaki
          bilgilerle bizimle iletişime geçin:
        </p>
        <div className="contact-info">
          <p><strong>E-posta:</strong> legal@akikkuyumculuk.com</p>
          <p><strong>Konu:</strong> Telif Hakkı Bildirimi</p>
        </div>
      </section>

      <section>
        <h2>Yasal İşlem</h2>
        <p>
          Telif hakkı ihlalleri yasal işleme tabi tutulacaktır. İhlal durumunda tazminat
          talep etme hakkımız saklıdır.
        </p>
      </section>
    </div>
  </div>
);

export default Legal;
